import { $event } from "@/core/event";
import { SdkPlugin } from "@/core/plugin";
import { $event_subscribe } from "@/core/subscribe";

import type { ISdkPluginClass, SdkContext } from "@/interface/sdk";

type MessageParams = { [key: string]: string | number | MessageParams };

interface PostMessageParams {
  type: string;
  payload: MessageParams[];
}

export interface MessageApi {
  /**
   * 向父窗口发送消息
   *
   * @param t - 要发送的数据
   * @param r - 目标源，默认为 "*"
   *
   * @example
   * ```typescript
   * $event.post({ type: 'hello', message: 'world' });
   * ```
   */
  post<T>(t: T, r?: string): void;

  /**
   * 向指定 iframe 发送消息
   *
   * @param iframe - 目标 iframe 元素
   * @param t - 要发送的数据
   * @param r - 目标源，默认为 "*"
   *
   * @example
   * ```typescript
   * const iframe = document.querySelector('iframe');
   * $event.postIframe(iframe, { type: 'data' });
   * ```
   */
  postIframe<T>(iframe: HTMLIFrameElement, t: T, r?: string): void;

  onMessage<T>(cn: (s: T) => void): void;
  offMessage<T>(cn: (s: T) => void): void;
  clearMessages(): void;
}

export class MessagePlugin extends SdkPlugin implements MessageApi {
  static pluginName = "Message" as const;
  static requires: ISdkPluginClass[] = [];

  private messages = new Set<(s: unknown) => void>();

  init(_ctx: SdkContext): void {
    $event_subscribe.on("message").subscribe((o) => {
      for (const fn of this.messages) {
        fn(o);
      }
    });
  }

  onMessage<T>(cn: (s: T) => void): void {
    this.messages.add(cn as (s: unknown) => void);
  }

  // 新增：删除指定的消息监听函数
  offMessage<T>(cn: (s: T) => void): void {
    this.messages.delete(cn as (s: unknown) => void);
  }

  // 可选：添加一个清除所有监听器的方法
  clearMessages(): void {
    this.messages.clear();
  }

  post<T>(t: T | PostMessageParams, r: string = "*"): void {
    $event.post(t, r);
  }

  postIframe<T>(
    iframe: HTMLIFrameElement,
    t: T | PostMessageParams,
    r: string = "*",
  ): void {
    $event.postIframe(iframe, t, r);
  }
}

/**
 * 模块扩展：将 MessagePlugin 注册到插件注册表
 *
 * 使用 typeof 自动从 pluginName 推断类型，确保插件名称只需定义一次。
 */
declare module "@/interface/plugin-registry" {
  interface PluginRegistry {
    [MessagePlugin.pluginName]: MessagePlugin;
  }
}
